import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Chip,
  Typography,
  Paper,
  IconButton,
  Alert,
  CircularProgress,
  Tooltip,
  Popover
} from '@mui/material';
import { FilterList, Clear } from '@mui/icons-material';
import { CategoryInfo, CatalogFilters } from '../types';
import { getCategories } from '../services/api';

interface CategoryFilterProps {
  filters: CatalogFilters;
  onFiltersChange: (filters: CatalogFilters) => void;
  disabled?: boolean;
}

export const CategoryFilter: React.FC<CategoryFilterProps> = ({
  filters,
  onFiltersChange,
  disabled = false
}) => {
  const [categories, setCategories] = useState<CategoryInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCategories = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await getCategories();
        setCategories(response.categories);
      } catch (err) {
        setError('Failed to load categories');
        console.error('Error fetching categories:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  const handleCategoryChange = (event: SelectChangeEvent<string>) => {
    const newCategory = event.target.value || undefined;
    onFiltersChange({
      ...filters,
      category: newCategory,
      subCategory: undefined
    });
  };

  const handleSubCategoryChange = (event: SelectChangeEvent<string>) => {
    const newSubCategory = event.target.value || undefined;
    onFiltersChange({
      ...filters,
      subCategory: newSubCategory
    });
  };

  const selectedCategory = categories.find(cat => cat.category === filters.category);
  const availableSubCategories = selectedCategory?.subCategories || [];
  const hasActiveFilters = filters.category || filters.subCategory;
  
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);
  const id = open ? 'category-filter-popover' : undefined;

  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Tooltip title="Filter">
        <IconButton
          ref={buttonRef}
          aria-describedby={id}
          onClick={handleClick}
          disabled={disabled}
          sx={{
            width: 'min-content',
            height: 'min-content',
            '&:hover': {
              bgcolor: 'action.hover',
            },
            '&.Mui-disabled': {
              opacity: 0.5,
            }
          }}
        >
          <FilterList />
        </IconButton>
      </Tooltip>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            ml: -1,
            minWidth: { xs: 300, sm: 400 },
            maxWidth: { xs: '90vw', sm: 450 },
            width: { xs: '90vw', sm: 'auto' },
          }
        }}
      >
        <Paper elevation={1} sx={{ p: 2, mb: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {hasActiveFilters && (
                <Chip
                  label={`${filters.category ? '1' : '0'} + ${filters.subCategory ? '1' : '0'} active`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                size="small"
                onClick={handleClose}
                disabled={disabled}
                title="Close"
              >
                <Clear />
              </IconButton>
            </Box>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Box sx={{
              display: 'flex',
              gap: 2,
              flexDirection: { xs: 'column', sm: 'row' },
              flexWrap: 'wrap'
            }}>

              {/* Category Filter */}
              <FormControl sx={{
                minWidth: { xs: '100%', sm: 200 },
                flex: { xs: 'none', sm: 1 }
              }}>
                <InputLabel id="category-select-label">Category</InputLabel>
                <Select
                  labelId="category-select-label"
                  value={filters.category || ''}
                  onChange={handleCategoryChange}
                  label="Category"
                  disabled={disabled || categories.length === 0}
                >
                  <MenuItem value="">
                    <em>All Categories</em>
                  </MenuItem>
                  {categories.map((category) => (
                    <MenuItem key={category.category} value={category.category}>
                      {category.category} ({category.subCategories.length})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Subcategory Filter */}
              <FormControl sx={{
                minWidth: { xs: '100%', sm: 200 },
                flex: { xs: 'none', sm: 1 }
              }}>
                <InputLabel id="subcategory-select-label">Subcategory</InputLabel>
                <Select
                  labelId="subcategory-select-label"
                  value={filters.subCategory || ''}
                  onChange={handleSubCategoryChange}
                  label="Subcategory"
                  disabled={disabled || !filters.category || availableSubCategories.length === 0}
                >
                  <MenuItem value="">
                    <em>All Subcategories</em>
                  </MenuItem>
                  {availableSubCategories.map((subCategory) => (
                    <MenuItem key={subCategory} value={subCategory}>
                      {subCategory}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          {/* Active Filters Display */}
          {hasActiveFilters && (
            <Box sx={{
              mt: 2,
              display: 'flex',
              gap: 1,
              flexWrap: 'wrap',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' },
              justifyContent: { xs: 'flex-start', sm: 'center' }
            }}>
              <Typography variant="body2" color="text.secondary" sx={{ alignSelf: { xs: 'flex-start', sm: 'auto' } }}>
                Active filters:
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' } }}>
                {filters.category && (
                  <Chip
                    label={`Category: ${filters.category}`}
                    size="small"
                    onDelete={() => onFiltersChange({ ...filters, category: undefined, subCategory: undefined })}
                    disabled={disabled}
                    sx={{ flex: { xs: 1, sm: 'none' } }}
                  />
                )}
                {filters.subCategory && (
                  <Chip
                    label={`Subcategory: ${filters.subCategory}`}
                    size="small"
                    onDelete={() => onFiltersChange({ ...filters, subCategory: undefined })}
                    disabled={disabled}
                    sx={{ flex: { xs: 1, sm: 'none' } }}
                  />
                )}
              </Box>
            </Box>
          )}
        </Paper>
      </Popover>
    </>
  );
};
